import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

export default function ParallaxGrowProjects({
  projects = [],
  sectionVh = 220,
  lastHoldVh = 100,
  bgBlurPx = 0, 
  className = "",
}) {
  const rootRef = useRef(null);
  const mediaRef = useRef(null);
  const videoRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [cardProjectIndex, setCardProjectIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const sectionProgress = useMotionValue(0);
  const smooth = sectionProgress;

  // Cover reveal is no longer tied directly to smooth
  const coverReveal = useMotionValue(0);
  const nextCoverHeight = useTransform(coverReveal, [0, 1], ["0%", "100%"]);

  const nProjects = projects.length;
  const transitionsCount = Math.max(0, nProjects - 1);

  const isLast = nProjects > 0 && activeIndex >= nProjects - 1;
  const nextIdx = Math.min(activeIndex + 1, Math.max(0, nProjects - 1));

  // Background strip moves 0% -> -50% (strip is 200vh)
  const stackY = useTransform(smooth, [0, 1], ["0%", "-50%"]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    let raf = 0;

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (nProjects === 0) return;

        const rect = el.getBoundingClientRect();
        const top = rect.top;

        const transitionPx = (window.innerHeight * sectionVh) / 100;
        const holdPx = (window.innerHeight * lastHoldVh) / 100;

        const totalPx = transitionsCount * transitionPx + holdPx;
        const denom = Math.max(1, totalPx - window.innerHeight);

        const raw = clamp01((0 - top) / denom);
        const pxInto = raw * denom;

        // Last hold: freeze animation and show last project
        if (pxInto >= transitionsCount * transitionPx || transitionsCount === 0) {
          const last = nProjects - 1;
          setActiveIndex((prev) => (prev === last ? prev : last));
          setCardProjectIndex((prev) => (prev === last ? prev : last));
          sectionProgress.set(0);
          coverReveal.set(1); // fully covered by final project cover
          return;
        }

        const idx = Math.min(
          transitionsCount - 1,
          Math.max(0, Math.floor(pxInto / transitionPx))
        );

        const localPx = pxInto - idx * transitionPx;
        let local = clamp01(localPx / transitionPx);

        if (local > 0.999) local = 0.999;

        // Card identity switches at halfway (keeps text/video aligned with your earlier behavior)
        const desiredCard = local >= 0.5 ? Math.min(idx + 1, nProjects - 1) : idx;

        setActiveIndex((prev) => (prev === idx ? prev : idx));
        setCardProjectIndex((prev) => (prev === desiredCard ? prev : desiredCard));
        sectionProgress.set(local);

        // ---- Cover reveal starts only when background boundary touches the card ----
        // Boundary between bg1 and bg2 in viewport px:
        // local=0 => boundary at bottom of viewport (innerHeight)
        // local=1 => boundary at top (0)
        const boundaryY = (1 - local) * window.innerHeight;

        const mediaEl = mediaRef.current;
        if (mediaEl) {
          const m = mediaEl.getBoundingClientRect();
          const cardTop = m.top;
          const cardBottom = m.bottom;
          const cardH = Math.max(1, cardBottom - cardTop);

          // Start revealing when boundary hits the card bottom
          // Fully revealed when boundary reaches card top
          const reveal = clamp01((cardBottom - boundaryY) / cardH);

          coverReveal.set(reveal);
        } else {
          coverReveal.set(0);
        }
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [
    nProjects,
    transitionsCount,
    sectionVh,
    lastHoldVh,
    sectionProgress,
    coverReveal,
  ]);

  // Hover play/pause
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (isHovering) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      v.pause();
      try {
        v.currentTime = 0;
      } catch {}
    }
  }, [isHovering, cardProjectIndex]);

  if (nProjects === 0) {
    return <div className={"pg__empty " + className}>Provide at least one project</div>;
  }

  const active = projects[activeIndex] || projects[0];
  const next = projects[nextIdx] || active;
  const cardProject = projects[cardProjectIndex] || projects[0];

  const activeCoverSrc = active.coverSrc || active.bgSrc;
  const nextCoverSrc = next.coverSrc || next.bgSrc;

  const totalHeightVh = transitionsCount * sectionVh + lastHoldVh;

  return (
    <div
      ref={rootRef}
      className={"pg__root " + className}
      style={{ height: `${totalHeightVh}vh` }}
    >
      <div className="pg__sticky">
        <div className="pg__bgStack">
          {!isLast ? (
            <motion.div className="pg__bgStrip" style={{ y: stackY }}>
              <div className="pg__bgFrame">
                <img
                  src={active.bgSrc}
                  alt={active.title || "Background"}
                  className="pg__bgImg"
                  style={{ filter: bgBlurPx ? `blur(${bgBlurPx}px)` : "none" }}
                  draggable={false}
                />
              </div>

              <div className="pg__bgFrame">
                <img
                  src={next.bgSrc}
                  alt={next.title || "Next background"}
                  className="pg__bgImg"
                  style={{ filter: bgBlurPx ? `blur(${bgBlurPx}px)` : "none" }}
                  draggable={false}
                />
              </div>
            </motion.div>
          ) : (
            <div className="pg__bgSingle">
              <img
                src={projects[nProjects - 1].bgSrc}
                alt={projects[nProjects - 1].title || "Background"}
                className="pg__bgImg"
                style={{ filter: bgBlurPx ? `blur(${bgBlurPx}px)` : "none" }}
                draggable={false}
              />
            </div>
          )}

          <div className="pg__vignette" />
        </div>

        <div className="pg__fg">


  <div className="pg__layout">
    <div className="pg__left">
      <div className="pg__cardWrap">
        <div className="pg__card">
              <div
                ref={mediaRef}
                className="pg__media"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {/* Video underneath, only visible on hover */}
                <video
                  ref={videoRef}
                  key={`video-${cardProject.id || cardProjectIndex}`}
                  className="pg__video"
                  src={cardProject.videoSrc}
                  muted
                  playsInline
                  preload="metadata"
                  loop
                  style={{ opacity: isHovering ? 1 : 0 }}
                />

                {/* Cover stack on top when not hovering */}
                <div className="pg__coverStack" style={{ opacity: isHovering ? 0 : 1 }}>
                  <img
                    src={activeCoverSrc}
                    alt={active.title || "Cover"}
                    className="pg__coverImg"
                    draggable={false}
                  />

                  {!isLast ? (
                    <motion.div
                      className="pg__coverNext"
                      style={{ height: nextCoverHeight }}
                    >
                      <img
                        src={nextCoverSrc}
                        alt={next.title || "Next cover"}
                        className="pg__coverImg"
                        draggable={false}
                      />
                    </motion.div>
                  ) : (
                    <div className="pg__coverNext" style={{ height: "100%" }}>
                      <img
                        src={projects[nProjects - 1].coverSrc || projects[nProjects - 1].bgSrc}
                        alt={projects[nProjects - 1].title || "Final cover"}
                        className="pg__coverImg"
                        draggable={false}
                      />
                    </div>
                  )}
                </div>

                <div className="pg__sheen" />
              </div>
              </div>
      </div>
    </div>

    <div className="pg__right">
      <div className="pg__rightTitleBlock">
        <div className="pg__rightKicker">[{cardProjectIndex + 1}]</div>
        <div className="pg__rightTitle">{cardProject.title || "Codex"}</div>
      </div>

      <div className="pg__bottomMeta">
        {/* <div className="pg__tagList">
          <div>WEB DESIGN</div>
          <div>WEBFLOW DEV</div>
          <div>ANIMATION</div>
          <div>ART DIRECTION</div>
          <div>3D CREATION</div>
        </div> */}
        <p>{cardProject.subtitle}</p>

        <Link
            className="pg__viewLink"
            to={`/projects/${cardProject.slug}`}
            >
            VIEW PROJECT <span aria-hidden>↗</span>
        </Link>

      </div>
    </div>
  </div>

</div>

      </div>
    </div>
  );
}
